import { useState, useEffect, useCallback } from 'react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import PlatformBadge from './components/PlatformBadge';
import MktAccountDrawer from './MktAccountDrawer';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';
import { listMktAccounts, syncMktAccount, deleteMktAccount } from 'src/actions/v2/marketing/analytics/v2_actionMktAccounts';

const STATUS_MAP = {
    active: { label: 'Ativo', dotColor: 'bg-green-500', className: d.statusBadge.active },
    expired: { label: 'Expirado', dotColor: 'bg-red-500', className: d.statusBadge.expired },
    disconnected: { label: 'Desconectado', dotColor: 'bg-gray-500', className: d.statusBadge.disconnected }
};

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

function formatDate(dateStr) {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const MKT_OAUTH_CHANNEL = 'mkt_oauth_channel';
const MKT_OAUTH_STORAGE_KEY = 'mkt_oauth_code';

export default function MktAccounts() {
    const { user } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Handle OAuth popup callback: if URL has ?code=, send it to parent and close
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (!code) return;

        // Send code to parent window via all channels
        try {
            const bc = new BroadcastChannel(MKT_OAUTH_CHANNEL);
            bc.postMessage({ type: 'MKT_OAUTH_CODE', code });
            bc.close();
        } catch (_) {}

        try {
            if (window.opener) {
                window.opener.postMessage({ type: 'MKT_OAUTH_CODE', code }, window.location.origin);
            }
        } catch (_) {}

        localStorage.setItem(MKT_OAUTH_STORAGE_KEY, code);

        // Clean URL and try to close popup
        window.history.replaceState({}, '', window.location.pathname);
        try { window.close(); } catch (_) {}
    }, []);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listMktAccounts(user.clienteId);
            setAccounts(response.data?.data || []);
        } catch (err) {
            console.error('Erro ao carregar contas:', err);
        } finally {
            setLoading(false);
        }
    }, [user.clienteId]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const handleSync = async (accountId) => {
        try {
            await syncMktAccount(accountId);
            fetchAccounts();
        } catch (err) {
            console.error('Erro ao sincronizar conta:', err);
        }
    };

    const handleDelete = async (accountId) => {
        try {
            await deleteMktAccount(accountId);
            setAccounts((prev) => prev.filter((a) => a._id !== accountId));
        } catch (err) {
            console.error('Erro ao desconectar conta:', err);
        }
    };

    const handleDrawerRefresh = () => {
        fetchAccounts();
    };

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.wrapper}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconBox}>
                        <Iconify icon="eva:people-outline" width={28} className={d.page.header.icon} />
                    </div>
                    <div>
                        <h1 className={d.page.header.title}>Contas Sociais</h1>
                        <p className={d.page.header.subtitle}>Gerencie suas conexões com redes sociais</p>
                    </div>
                </div>
                <button className={d.page.header.actionButton} onClick={() => setDrawerOpen(true)}>
                    <Iconify icon="eva:plus-outline" width={18} />
                    Conectar Conta
                </button>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-20">
                    <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full w-1/2 bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" />
                    </div>
                </div>
            )}

            {/* Accounts Grid */}
            {!loading && accounts.length > 0 && (
                <div className={d.accounts.grid}>
                    {accounts.map((account) => {
                        const status = STATUS_MAP[account.status] || STATUS_MAP.disconnected;
                        const followers = account.account_info?.followers_count || account.account_info?.fans_count || 0;
                        const posts = account.account_info?.media_count || 0;

                        return (
                            <div key={account._id} className={d.accounts.card}>
                                {/* Card Header */}
                                <div className={d.accounts.cardHeader}>
                                    <div className="relative">
                                        {account.profile_picture ? (
                                            <img src={account.profile_picture} alt={account.account_name} className={d.accounts.avatar} />
                                        ) : (
                                            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                                                <Iconify icon="eva:person-outline" width={24} className="text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div className={d.accounts.cardInfo}>
                                        <p className={d.accounts.cardName}>{account.account_name}</p>
                                        <p className={d.accounts.cardUsername}>{account.account_info?.username ? `@${account.account_info.username}` : account.account_info?.category || account.provider}</p>
                                    </div>
                                    <span className={status.className}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${status.dotColor}`} />
                                        {status.label}
                                    </span>
                                </div>

                                {/* Platform Badge */}
                                <div className="mb-3">
                                    <PlatformBadge platform={account.provider} />
                                </div>

                                {/* Stats */}
                                <div className={d.accounts.cardStats}>
                                    <div className={d.accounts.cardStat}>
                                        <p className={d.accounts.cardStatValue}>{formatNumber(followers)}</p>
                                        <p className={d.accounts.cardStatLabel}>Seguidores</p>
                                    </div>
                                    <div className={d.accounts.cardStat}>
                                        <p className={d.accounts.cardStatValue}>{formatNumber(posts)}</p>
                                        <p className={d.accounts.cardStatLabel}>Publicações</p>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className={d.accounts.cardFooter}>
                                    <span className={d.accounts.syncText}>
                                        Sincronizado: {formatDate(account.last_sync)}
                                    </span>
                                    <div className="flex items-center gap-3">
                                        <button className={d.accounts.syncBtn} onClick={() => handleSync(account._id)}>
                                            <Iconify icon="eva:refresh-outline" width={14} className="inline mr-1" />
                                            Sincronizar
                                        </button>
                                        <button
                                            className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                                            onClick={() => handleDelete(account._id)}
                                        >
                                            Desconectar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Empty State */}
            {!loading && accounts.length === 0 && (
                <div className={d.emptyState.container}>
                    <div className={d.emptyState.iconBox}>
                        <Iconify icon="eva:link-2-outline" width={28} className="text-violet-600" />
                    </div>
                    <h3 className={d.emptyState.title}>Nenhuma conta conectada</h3>
                    <p className={d.emptyState.subtitle}>
                        Conecte suas redes sociais para gerenciar publicações e acompanhar métricas em um só lugar.
                    </p>
                    <span className={d.emptyState.action} onClick={() => setDrawerOpen(true)}>
                        Conectar primeira conta
                    </span>
                </div>
            )}

            {/* Drawer */}
            <MktAccountDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onRefresh={handleDrawerRefresh}
            />
        </div>
    );
}
